// ** React Imports
import { useEffect, useRef, useState } from 'react';

import { client } from 'src/utils/axios-client';
import {
    LeadScreenStore,
    LeadActivity,
    Lead,
    NextActivity,
    LeadAccountStore,
    ViewType,
    ActivityDialogStore,
    LeadCompanyStore,
    CompanyDialogStore,
    LeadSprintStore,
    SprintDialogStore,
    LeadActivityHistoryStore,
    ActivityHistoryDialogStore,
    LeadActivityCadenceStore,
    CadenceListDialogStore,
    LeadActivityOutcomeStore,
    ActivityOutcomes,
    TwilioStore,
    CallButton,
} from 'src/stores/LeadStore';
import { getActivityEndpoint, leadEndpoints } from '../../../configs/endpoints';

// ** MUI Imports
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import _ from 'lodash';
import Badge, { BadgeProps } from '@mui/material/Badge';
import Button from '@mui/material/Button';
import Icon from 'src/features/common/CustomIcon';
import MenuItem from '@mui/material/MenuItem';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import ListItemIcon from '@mui/material/ListItemIcon';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { ProblemDetails } from '../../../types/ApiErrorResponse';
import SaveTwoToneIcon from '@mui/icons-material/SaveTwoTone';
import FormControl from '@mui/material/FormControl';
import LocalPhoneTwoToneIcon from '@mui/icons-material/LocalPhoneTwoTone';
import InputAdornment from '@mui/material/InputAdornment';
import Alert from '@mui/material/Alert';
import DoActivityOutcomes from './DoActivityOutcomes';
import PhoneInTalkTwoToneIcon from '@mui/icons-material/PhoneInTalkTwoTone';
import PhoneControls from './PhoneControls';
import { callHangup, intitializeDeviceAndStartCall } from 'src/features/Twilio/TwilioBase';
import CallEndIcon from '@mui/icons-material/CallEnd';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';

import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import Skeleton from '@mui/material/Skeleton';
import StandardSkeleton from '../../common/StandardSkeleton';

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
    '& .MuiBadge-badge': {
        right: 33,
        top: 10,
        border: `2px solid ${theme.palette.background.paper}`,
        padding: '0 4px',
    },
}));

type StatusUpdate = { roomName: string; status: string };

const DoActivityOutboundPhoneCall = () => {
    const outcomesRecord = LeadActivityOutcomeStore.useState((s) => s.record);
    const [dataLoaded, setDataLoaded] = useState(false);
    const selectedLead = LeadScreenStore.useState((s) => s.selectedLead);
    const [phoneFrom, setPhoneFrom] = useState(outcomesRecord.phoneFrom);
    const [phoneTo, setPhoneTo] = useState(outcomesRecord.phoneTo);
    const twilioStore = TwilioStore.useState((s) => s.record);
    const showCallButton = CallButton.useState((s) => s.showCallButton);
    const showHangupButton = CallButton.useState((s) => s.showHangupButton);
    const showOtherButtons = CallButton.useState((s) => s.showOtherButtons);
    const showCallingButton = CallButton.useState((s) => s.showCallingButton);
    const [connection, setConnection] = useState<null | HubConnection>(null);
    const latestStatus = useRef({ roomName: '', status: '' } as StatusUpdate);
    const [status, setStatus] = useState<StatusUpdate>({ roomName: '', status: '' });
    const [roomName, setRoomName] = useState('');
    const [showMessageStatus, setShowMessageStatus] = useState(false);
    const [sentStatus, setSentStatus] = useState('');
    latestStatus.current = status;

    useEffect(() => {
        const fetchOutcomes = async () => {
            const response = await client.get(
                leadEndpoints.getactivityoutcomes.replace('{leadid}', '' + selectedLead?.id.toString()),
            );
            LeadActivityOutcomeStore.update((s) => {
                s.record = response.data;
            });
            setPhoneFrom(response.data.phoneFrom);
            setPhoneTo(response.data.phoneTo);
            setDataLoaded(true);
        };
        if (selectedLead != null) fetchOutcomes();
    }, []);

    const handlePhoneFromChange = (event: SelectChangeEvent) => {
        setPhoneFrom(event.target.value as string);
        LeadActivityOutcomeStore.update((s) => {
            s.record.phoneFrom = event.target.value;
        });
    };

    const handlePhoneToChange = (event: SelectChangeEvent) => {
        setPhoneTo(event.target.value as string);
        LeadActivityOutcomeStore.update((s) => {
            s.record.phoneTo = event.target.value;
        });
    };

    const makeCall = async () => {
        await client
            .get(leadEndpoints.gettwiliotoken)
            .then(async (response) => {
                CallButton.update((s) => {
                    s.showCallButton = false;
                });
                CallButton.update((s) => {
                    s.showCallingButton = true;
                });

                TwilioStore.update((s) => {
                    s.record = response.data;
                });

                intitializeDeviceAndStartCall(
                    response.data.token,
                    response.data.identity,
                    outcomesRecord.traceLevel,
                    phoneTo,
                    outcomesRecord.organizationId,
                    outcomesRecord.leadId,
                    outcomesRecord.userId,
                    outcomesRecord.callRecord,
                    outcomesRecord.roomName,
                );
            })
            .catch((err: ProblemDetails) => {
                if (err.errors) {
                    for (const [key, value] of Object.entries(err.errors)) {
                        const errorMessage = '';

                        value.forEach((message) => {
                            message += message + '\\';
                        });
                    }
                }
            });
    };

    const hangupCall = () => {
        setSentStatus('Call Ended.');
        setShowMessageStatus(true);
        callHangup();
    };

    useEffect(() => {
        const newConnection = new HubConnectionBuilder()
            //.withUrl('https://localhost:44344/hubs/twilio')
            .withUrl('/hubs/twilio')
            .withAutomaticReconnect()
            .build();

        setConnection(newConnection);
    }, []);

    useEffect(() => {
        if (connection) {
            connection
                .start()
                .then((result) => {
                    console.log('Connected!');

                    connection.on('SendConferenceRoom', (message) => {
                        const updatedStatus = { ...message };
                        setStatus(updatedStatus);
                    });
                })
                .catch((e) => console.log('Connection failed: ', e));
        }
    }, [connection]);

    useEffect(() => {
        if (status.roomName != '' && status.roomName.toLowerCase() == outcomesRecord.roomName.toLowerCase()) {
            switch (status.status) {
                case 'participant-join':
                    // code block
                    break;
                case 'Connecting':
                    setSentStatus('Establishing connection...');
                    LeadActivityOutcomeStore.update((s) => {
                        s.record.actionId = status.roomName;
                    });
                    setRoomName(status.roomName);
                    setShowMessageStatus(true);
                    break;
                case 'conference-start':
                    // code block
                    break;
                case 'conference-end':
                    setSentStatus('Call Ended.');
                    setShowMessageStatus(true);
                    break;
                case 'initiated':
                    // code block
                    break;
                case 'ringing':
                    setSentStatus('Ringing...');
                    setShowMessageStatus(true);
                    break;
                case 'in-progress':
                    CallButton.update((s) => {
                        s.showOtherButtons = true;
                    });
                    setSentStatus('In progress...');
                    setShowMessageStatus(true);
                    break;
                case 'completed':
                    CallButton.update((s) => {
                        s.showOtherButtons = false;
                    });
                    setSentStatus('Call Ended.');
                    setShowMessageStatus(true);
                    break;
                default:
            }
        }
    }, [status]);

    if (dataLoaded) {
        return (
            <Box>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Card variant="outlined">
                            <CardContent>
                                <Box>
                                    <Grid container alignContent="left" spacing={2}>
                                        <Grid item xs={12} sx={{ alignItems: 'right' }} justifyContent={'flex-end'}>
                                            <Typography sx={{ fontSize: 20, color: 'CornflowerBlue' }} gutterBottom>
                                                Outgoing Phone Call
                                            </Typography>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            md={5}
                                            alignContent="left"
                                            justifyContent={'left'}
                                            sx={{ pt: 1, display: 'flex', alignItems: 'left', flexDirection: 'column' }}
                                        >
                                            <FormControl>
                                                <InputLabel htmlFor="phoneFrom" id="-label">
                                                    Your phone number
                                                </InputLabel>
                                                <Select
                                                    id="phoneFrom"
                                                    label="Your phone number"
                                                    value={phoneFrom}
                                                    size="small"
                                                    fullWidth
                                                    displayEmpty
                                                    onChange={handlePhoneFromChange}
                                                >
                                                    {outcomesRecord.userPhoneNumbers.map((record) => (
                                                        <MenuItem key={record.sid} value={record.number}>
                                                            <Grid container justifyContent={'left'}>
                                                                <Grid item xs={12} md={2}>
                                                                    <Tooltip title="">
                                                                        <ListItemIcon>
                                                                            <SaveTwoToneIcon sx={{ opacity: 0 }} />
                                                                        </ListItemIcon>
                                                                    </Tooltip>
                                                                </Grid>
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    md={10}
                                                                    justifyContent={'left'}
                                                                    alignItems="left"
                                                                >
                                                                    <Tooltip
                                                                        title={
                                                                            record.countryName + ' ' + record.location
                                                                        }
                                                                        key={record.outcomeId}
                                                                    >
                                                                        <Typography
                                                                            variant="inherit"
                                                                            noWrap
                                                                            align="left"
                                                                        >
                                                                            {record.number}
                                                                        </Typography>
                                                                    </Tooltip>
                                                                </Grid>
                                                            </Grid>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            md={5}
                                            alignContent="left"
                                            justifyContent={'left'}
                                            sx={{ pt: 1, display: 'flex', alignItems: 'left', flexDirection: 'column' }}
                                        >
                                            {!outcomesRecord.noLeadNumbers && (
                                                <FormControl fullWidth>
                                                    <InputLabel htmlFor="phoneTo" id="-label2">
                                                        Number to call
                                                    </InputLabel>
                                                    <Select
                                                        id="phoneTo"
                                                        label="Number to call"
                                                        value={phoneTo}
                                                        size="small"
                                                        fullWidth
                                                        displayEmpty
                                                        onChange={handlePhoneToChange}
                                                    >
                                                        {outcomesRecord.leadPhoneNumbers.map((record) => (
                                                            <MenuItem key={record.number} value={record.number}>
                                                                <Grid container spacing={2} justifyContent={'left'}>
                                                                    <Grid item xs={12} md={2}>
                                                                        {record.numberIcon && (
                                                                            <Tooltip
                                                                                title={record.numberType}
                                                                                key={record.number}
                                                                            >
                                                                                <ListItemIcon>
                                                                                    <Icon
                                                                                        icon={record.numberIcon}
                                                                                    ></Icon>
                                                                                </ListItemIcon>
                                                                            </Tooltip>
                                                                        )}
                                                                    </Grid>
                                                                    <Grid
                                                                        item
                                                                        xs={12}
                                                                        md={10}
                                                                        justifyContent={'left'}
                                                                        alignItems="left"
                                                                    >
                                                                        <Tooltip
                                                                            title={record.numberType}
                                                                            key={record.number}
                                                                        >
                                                                            <Typography
                                                                                variant="inherit"
                                                                                noWrap
                                                                                align="left"
                                                                            >
                                                                                &nbsp; {record.number}
                                                                            </Typography>
                                                                        </Tooltip>
                                                                    </Grid>
                                                                </Grid>
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            )}
                                            {outcomesRecord.noLeadNumbers && (
                                                <Tooltip title="There are no valid numbers to call, this is because they have not been assigned or they have been marked as DNC (do not call).">
                                                    <Alert severity="error">No valid lead phone numbers!</Alert>
                                                </Tooltip>
                                            )}
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            md={2}
                                            alignItems="center"
                                            sx={{ pt: 1, display: 'flex', flexDirection: 'row' }}
                                        >
                                            {showCallButton && dataLoaded && !outcomesRecord.noLeadNumbers && (
                                                <Tooltip title="Make Phone Call">
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="success"
                                                        startIcon={<LocalPhoneIcon />}
                                                        sx={{ mr: 3 }}
                                                        onClick={makeCall}
                                                    >
                                                        Call
                                                    </Button>
                                                </Tooltip>
                                            )}
                                            {showCallingButton && (
                                                <Tooltip title="Calling">
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="info"
                                                        startIcon={<PhoneInTalkTwoToneIcon />}
                                                        sx={{ mr: 3 }}
                                                        disabled={true}
                                                    >
                                                        Calling
                                                    </Button>
                                                </Tooltip>
                                            )}
                                            {showHangupButton && (
                                                <Tooltip title="Hangup Phone Call">
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="error"
                                                        startIcon={<CallEndIcon />}
                                                        sx={{ mr: 3 }}
                                                        onClick={hangupCall}
                                                    >
                                                        Hangup
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </Grid>
                                        {showMessageStatus && (
                                            <Grid
                                                item
                                                xs={12}
                                                md={12}
                                                alignContent="left"
                                                justifyContent={'left'}
                                                sx={{
                                                    pt: 1,
                                                    display: 'flex',
                                                    alignItems: 'left',
                                                    flexDirection: 'column',
                                                }}
                                            >
                                                <Alert severity="info">{sentStatus}</Alert>
                                            </Grid>
                                        )}
                                        {dataLoaded && showOtherButtons && (
                                            <Grid
                                                item
                                                xs={12}
                                                md={12}
                                                alignContent="left"
                                                justifyContent={'left'}
                                                sx={{
                                                    pt: 1,
                                                    display: 'flex',
                                                    alignItems: 'left',
                                                    flexDirection: 'column',
                                                }}
                                            >
                                                <PhoneControls IsInbound={false} />
                                            </Grid>
                                        )}
                                        <Grid
                                            item
                                            xs={12}
                                            md={12}
                                            alignContent="left"
                                            justifyContent={'left'}
                                            sx={{ pt: 1, display: 'flex', alignItems: 'left', flexDirection: 'column' }}
                                        >
                                            {dataLoaded && <DoActivityOutcomes />}
                                        </Grid>
                                    </Grid>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        );
    } else {
        return <StandardSkeleton />;
    }
};

export { DoActivityOutboundPhoneCall };

export default DoActivityOutboundPhoneCall;
